import { Fragment, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { Link } from 'react-router-dom';

import { Container } from '@/components/container';
import { DashboardLanguageSelect } from '@/components/dashboard/DashboardLanguageSelect';
import {
  Toolbar,
  ToolbarActions,
  ToolbarHeading,
} from '@/layouts/demo1/toolbar';
import { useCurrentUserRoles } from '@/utils/userRole';
import { UserRoles } from '@/constant';

import { Demo1LightSidebarContent } from './';
import { ServiceProviderDashboard } from './ServiceProviderDashboard';
import { MarinaManagerDashboard } from './MarinaManagerDashboard';
import { RegionalOperatorDashboard } from './RegionalOperatorDashboard';

const getToolbarDescriptionId = (
  isServiceProvider: boolean,
  isMarinaManager: boolean,
  isRegionalOperator: boolean
): string => {
  if (isServiceProvider) {
    return 'DASHBOARD.DESCRIPTION.DEFAULT';
  }
  if (isMarinaManager) {
    return 'DASHBOARD.DESCRIPTION.MARINA';
  }
  if (isRegionalOperator) {
    return 'DASHBOARD.DESCRIPTION.REGIONAL';
  }
  return 'DASHBOARD.DESCRIPTION.DEFAULT';
};

const Demo1LightSidebarPage = () => {
  const intl = useIntl();
  const roles = useCurrentUserRoles();
  const isServiceProvider =
    roles.length > 0 &&
    roles.some((role) => role === UserRoles.SERVICE_PROVIDER);
  const isMarinaManager =
    roles.length > 0 && roles.some((role) => role === UserRoles.MARINA);
  const isRegionalOperator =
    roles.length > 0 &&
    roles.some((role) => role === UserRoles.REGIONAL_OPERATOR);

  const toolbarDescription = useMemo(
    () =>
      intl.formatMessage({
        id: getToolbarDescriptionId(
          isServiceProvider,
          isMarinaManager,
          isRegionalOperator
        ),
      }),
    [intl, isServiceProvider, isMarinaManager, isRegionalOperator]
  );

  const dashboardContent = useMemo(() => {
    if (isServiceProvider) {
      return <ServiceProviderDashboard />;
    }
    if (isMarinaManager) {
      return <MarinaManagerDashboard />;
    }
    if (isRegionalOperator) {
      return <RegionalOperatorDashboard />;
    }
    return <Demo1LightSidebarContent />;
  }, [isServiceProvider, isMarinaManager, isRegionalOperator]);

  return (
    <Fragment>
      <Container>
        <Toolbar>
          <ToolbarHeading
            id="dashboard-heading"
            title={intl.formatMessage({ id: 'DASHBOARD.TITLE' })}
            description={toolbarDescription}
          />
          <ToolbarActions>
            <DashboardLanguageSelect />
            <Link
              to="/account/home/user-profile"
              className="btn btn-sm btn-light"
            >
              {intl.formatMessage({ id: 'DASHBOARD.VIEW_PROFILE' })}
            </Link>
          </ToolbarActions>
        </Toolbar>
      </Container>

      <Container>{dashboardContent}</Container>
    </Fragment>
  );
};

export { Demo1LightSidebarPage };
